import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Loader2, FileText, Shield, Info, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import RichTextEditor from '../components/RichTextEditor';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAGE_DEFS = [
  { slug: 'privacy', icon: Shield, labelEs: 'Politica de Privacidad', labelEn: 'Privacy Policy' },
  { slug: 'terms', icon: FileText, labelEs: 'Terminos de Servicio', labelEn: 'Terms of Service' },
  { slug: 'about', icon: Info, labelEs: 'Acerca de', labelEn: 'About Us' },
  { slug: 'contact', icon: Phone, labelEs: 'Contacto', labelEn: 'Contact' },
];

const AdminPages = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [activeSlug, setActiveSlug] = useState('privacy');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPage = async (slug) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/settings/pages/${slug}`);
      setTitle(res.data.title || '');
      setContent(res.data.content || '');
    } catch {
      setTitle('');
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(activeSlug);
  }, [activeSlug]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/pages/${activeSlug}`, { title, content });
      toast({
        title: language === 'es' ? 'Pagina guardada' : 'Page saved',
        description: PAGE_DEFS.find(p => p.slug === activeSlug)?.[language === 'es' ? 'labelEs' : 'labelEn'],
      });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const activeDef = PAGE_DEFS.find(p => p.slug === activeSlug);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/dashboard"
          className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'es' ? 'Volver al Panel' : 'Back to Dashboard'}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="page-title">
          {language === 'es' ? 'Paginas del Sitio' : 'Site Pages'}
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          {language === 'es'
            ? 'Edita el contenido de las paginas legales e informativas del sitio.'
            : 'Edit the content of the legal and informational pages.'}
        </p>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-56 flex-shrink-0">
            <div className="space-y-1">
              {PAGE_DEFS.map((pg) => {
                const Icon = pg.icon;
                const isActive = pg.slug === activeSlug;
                return (
                  <button
                    key={pg.slug}
                    onClick={() => setActiveSlug(pg.slug)}
                    data-testid={`tab-${pg.slug}`}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                      isActive
                        ? 'bg-purple-100 text-purple-800'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {language === 'es' ? pg.labelEs : pg.labelEn}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1">
            <Card className="border border-gray-200">
              <CardContent className="p-6">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#6949b6' }} />
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        {language === 'es' ? 'Titulo de la pagina' : 'Page title'}
                      </Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={activeDef?.[language === 'es' ? 'labelEs' : 'labelEn']}
                        data-testid="page-title-input"
                      />
                    </div>
                    <div className="mb-6">
                      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        {language === 'es' ? 'Contenido' : 'Content'}
                      </Label>
                      <RichTextEditor value={content} onChange={setContent} />
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="text-white"
                      style={{ backgroundColor: '#6949b6' }}
                      data-testid="save-page-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {language === 'es' ? 'Guardar' : 'Save'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPages;
